import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import EditIcon from "../../assets/admin/edit-icon.svg";
import DeleteModal from "../modal/DeleteModal";
import CloseIcon from "@mui/icons-material/Close";
import DatePicker from "react-datepicker";
import Client from "../../model/client";
import ApiService from "../../service/ApiService";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import Team from "../../model/team";
import Contractor from "../../model/contractor";
import ProjectType from "../../model/projectType";
import Estimate from "../../model/estimate";
import AlertService from "../../service/AlertService";
import FirebaseService from "../../service/FirebaseService";
import Project from "../../model/project";
import ToastMessages from "../../model/enum/ToastMessages";
import PageResultResponse from "../../model/pageResultResponse";
import Misc from "../../utils/Misc";
import Select, { StylesConfig } from "react-select";
import useStyles from "./styles";
import "react-datepicker/dist/react-datepicker.css";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "100% !important",
    borderRadius: "5px",
    padding: "0.5rem",
    marginRight: "0.5rem",
    borderColor: DarkGrey,
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
      ? PrimaryColor
      : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

const ProjectModal = ({ project, resetList, isRow }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = project != null;
  const id = edit ? project.id : null;
  const code = edit ? project.code : null;
  const [name, setName] = useState(edit ? project.name : null);
  const [description, setDescription] = useState(
    edit ? project.description : null
  );
  const [startDate, setStartDate] = useState(edit ? project.startDate : null);
  const [dueDate, setDueDate] = useState(edit ? project.dueDate : null);
  const [rate, setRate] = useState(edit ? project.rate : null);
  const createdBy = edit ? project.createdBy : localStorage.getItem("userName");
  const [statusId, setStatusId] = useState(edit ? project.statusId : 2);
  const [clientId, setClientId] = useState(edit ? project.clientId : "");
  const [assignedTo, setAssignedTo] = useState(
    edit ? project.assignedTo : null
  );
  const [estimateCode, setEstimateCode] = useState(
    edit ? project.estimateCode : ""
  );
  const [projectTypeId, setProjectTypeId] = useState(
    edit ? project.projectTypeId : ""
  );
  const [teamId, setTeamId] = useState(
    edit && project.teamId ? project.teamId : ""
  );
  const [contractorId, setContractorId] = useState(
    edit && project.contractorId ? project.contractorId : ""
  );
  //Lists
  const [clients, setClients] = useState<Client[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          fetchedClients,
          fetchedTeams,
          fetchedEstimates,
          fetchedProjectTypes,
          fetchedContractors,
        ] = await Promise.all([
          ApiService.get<PageResultResponse>(DatabaseEntities.CLIENTS),
          ApiService.get<PageResultResponse>(DatabaseEntities.TEAMS),
          ApiService.get<PageResultResponse>(
            `${DatabaseEntities.ESTIMATES}?statusId=20`
          ),
          ApiService.get<ProjectType[]>(DatabaseEntities.PROJECT_TYPES),
          ApiService.get<PageResultResponse>(DatabaseEntities.CONTRACTORS),
        ]);
        if (Misc.isArrayEmpty(fetchedClients.items)) return;
        setClients(fetchedClients.items);
        setTeams(fetchedTeams.items);
        setEstimates(fetchedEstimates.items);
        setProjectTypes(fetchedProjectTypes);
        setContractors(fetchedContractors.items);
      } catch (error) {
        AlertService.error("Error fetching data");
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) fetchData();
  }, [open]);

  const handleName = (e: any) => setName(e.target.value);
  const handleDescription = (e: any) => setDescription(e.target.value);

  const handleRate = (e: any) => {
    // Check if the value is a valid number
    const value = parseFloat(e.target.value);
    if (isNaN(value)) {
      setRate(null); // Set to null if not a valid number
    } else {
      setRate(value);
    }
  };

  const handleClientId = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setClientId("");
      return;
    }

    setClientId(e.value);
  };

  const handleProjectTypeId = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setProjectTypeId("");
      return;
    }
    setProjectTypeId(e.value);
  };

  const handleEstimateCode = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setEstimateCode("");
      return;
    }

    setEstimateCode(e.value);
  };

  const handleTeamId = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setTeamId("");
      return;
    }

    setTeamId(e.value);
  };

  const handleContractorId = (e: any) => {
    if (!e || !e.value || typeof e === "string") {
      setContractorId("");
      return;
    }

    setContractorId(e.value);
  };

  const invalidInput = () => {
    // Check if dueDate is before startDate
    if (dueDate && startDate && dueDate < startDate) {
      AlertService.warning("Due date cannot be before start date.");
      return true;
    }

    // Check if any required field is empty or invalid
    return (
      Misc.isNullOrEmpty(name) ||
      clientId === "" ||
      projectTypeId === "" ||
      statusId === "" ||
      rate === null ||
      rate <= 0 ||
      estimateCode === ""
    );
  };

  const handleCreate = async (payload: Project): Promise<Project | null> => {
    try {
      const response = await ApiService.post<Project>(
        DatabaseEntities.PROJECTS,
        payload
      );
      AlertService.success(ToastMessages.CREATED);
      return response;
    } catch {
      AlertService.error(ToastMessages.CREATE_FAILED);
      return null;
    }
  };

  const handleUpdate = async (payload: Project) => {
    await ApiService.put<Project>(
      `${DatabaseEntities.PROJECTS}/${payload.id}`,
      payload
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Project) => {
    await ApiService.delete(`${DatabaseEntities.PROJECTS}/${payload.id}`)
      .then(async () => {
        await FirebaseService.deleteAllFiles(`Projects/${project.code}`).catch(
          () => {
            AlertService.error(ToastMessages.IMAGES_ERROR_FIREBASE);
          }
        );
        AlertService.success(ToastMessages.DELETED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const handleSubmit = async () => {
    if (invalidInput()) return;

    setLoading(true);

    const payload: Project = {
      id,
      name,
      description,
      startDate,
      dueDate,
      rate,
      code,
      createdBy,
      statusId,
      clientId,
      assignedTo,
      estimateCode,
      projectTypeId,
      teamId,
      contractorId,
    };

    if (edit) {
      await handleUpdate(payload);
    } else {
      await handleCreate(payload);
    }

    setName(null);
    setDescription(null);
    setStartDate(null);
    setDueDate(null);
    setRate(null);
    setStatusId(0);
    setClientId(0);
    setAssignedTo(null);
    setEstimateCode(0);
    setProjectTypeId(0);
    setTeamId(0);
    setLoading(false);
    resetList();
    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Btn onClick={handleOpen} style={{ textTransform: "none" }}>
          <img src={EditIcon} alt="edit icon" />
          Edit
        </Btn>
      ) : (
        <Btn
          className={classes.addItemGrid}
          onClick={handleOpen}
          style={{
            height: isRow ? "280px" : "200px",
          }}
        >
          +
        </Btn>
      )}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          <Box sx={style}>
            {loading && <Spinner />}
            <div className={classes.headerDiv}>
              {edit ? (
                <Typography className={classes.title}>
                  <div className={classes.headerSubDiv2}>
                    <Typography className={classes.title}>Project</Typography>
                    <div className="vl" style={{ marginLeft: "1rem" }}></div>
                    <Typography
                      className={classes.subtitle}
                      style={{ marginLeft: "1rem" }}
                    >
                      Edit
                    </Typography>
                    <DeleteModal item={project} handleDelete={handleDelete} />
                  </div>
                </Typography>
              ) : (
                <div className={classes.headerSubDiv}>
                  <Typography className={classes.title}>Project</Typography>
                  <div className="vl"></div>
                  <Typography className={classes.subtitle}>
                    {edit ? " Edit" : " Setup"}
                  </Typography>
                </div>
              )}
              <Btn onClick={handleClose}>
                <CloseIcon />
              </Btn>
            </div>
            <hr />
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <Typography className={classes.label}>
                  Project name <span style={{ color: "red" }}>*</span>
                </Typography>
                <input
                  className={classes.input}
                  placeholder="Type the name of the project"
                  value={name}
                  onChange={handleName}
                />
              </div>
              <div className={classes.inputGroup}>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Start date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <DatePicker
                    className={classes.input}
                    placeholderText="Select date"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Due date <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <DatePicker
                    className={classes.input}
                    placeholderText="Select date"
                    selected={dueDate}
                    onChange={(date) => setDueDate(date)}
                  />
                </div>
              </div>
              <div className={classes.inputGroup}>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Client <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Select
                    styles={styles}
                    value={
                      clientId
                        ? {
                            value: clientId,
                            label: `${
                              clients.find((clt) => clt.id === clientId)
                                ?.firstName
                            } ${
                              clients.find((clt) => clt.id === clientId)
                                ?.lastName
                            }`,
                          }
                        : ""
                    }
                    isClearable={true}
                    onChange={handleClientId}
                    options={clients.map((clt: Client) => ({
                      value: clt.id,
                      label: `${clt.firstName} ${clt.lastName}`,
                    }))}
                    placeholder="Select client"
                  />
                </div>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Rate <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <input
                    className={classes.input}
                    placeholder="$10000.00"
                    value={rate}
                    onChange={handleRate}
                    type="number"
                    min={0}
                    step={0.01}
                  />
                </div>
              </div>
              <div className={classes.inputGroup}>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Project type <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Select
                    styles={styles}
                    value={
                      projectTypeId
                        ? {
                            value: projectTypeId,
                            label: `${
                              projectTypes.find((pt) => pt.id === projectTypeId)
                                ?.name
                            }`,
                          }
                        : ""
                    }
                    isClearable={true}
                    onChange={handleProjectTypeId}
                    options={projectTypes.map((projectType) => ({
                      value: projectType.id,
                      label: `${projectType.name}`,
                    }))}
                    placeholder="Select project type"
                  />
                </div>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>
                    Estimate <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Select
                    styles={styles}
                    value={
                      estimateCode
                        ? {
                            value: estimateCode,
                            label: `${
                              estimates.find((est) => est.code === estimateCode)
                                ?.code
                            }`,
                          }
                        : ""
                    }
                    isClearable={true}
                    onChange={handleEstimateCode}
                    options={estimates.map((estimate) => ({
                      value: estimate.code,
                      label: `${estimate.code}`,
                    }))}
                    placeholder="Select project type"
                  />
                </div>
              </div>
              <div className={classes.inputGroup}>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>Team</Typography>
                  <Select
                    styles={styles}
                    value={
                      teamId
                        ? {
                            value: teamId,
                            label: `${
                              teams.find((team) => team.id === teamId)?.name
                            }`,
                          }
                        : ""
                    }
                    isClearable={true}
                    onChange={handleTeamId}
                    options={teams.map((team) => ({
                      value: team.id,
                      label: `${team.name}`,
                    }))}
                    placeholder="Select team"
                  />
                </div>
                <div className={classes.inputContainer}>
                  <Typography className={classes.label}>Contractor</Typography>
                  <Select
                    styles={styles}
                    value={
                      contractorId
                        ? {
                            value: contractorId,
                            label: `${
                              contractors.find((ct) => ct.id === contractorId)
                                ?.firstName
                            }
                            ${
                              contractors.find((ct) => ct.id === contractorId)
                                ?.lastName
                            }`,
                          }
                        : ""
                    }
                    isClearable={true}
                    onChange={handleContractorId}
                    options={contractors.map((contrt) => ({
                      value: contrt.id,
                      label: `${contrt.firstName} ${contrt.lastName}`,
                    }))}
                    placeholder="Select contractor"
                  />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <Typography className={classes.label}>Description</Typography>
                <textarea
                  className={classes.textarea}
                  value={description}
                  onChange={handleDescription}
                  placeholder="Add description here..."
                />
              </div>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={handleSubmit}
                disabled={invalidInput()}
              >
                {edit ? "Save" : "Create"}
              </Btn>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default ProjectModal;
